import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRegistrationForm, saveProfile } from '../api/endpoints'
import { ApiError } from '../api/client'
import type { Details } from '../api/types'
import { ErrorNotice } from '../components'
import DetailsForm from '../forms/DetailsForm'
import { toAnswers } from '../lib/answers'

/** For someone an admin invited: the same questions the campaign asks anyone who registers. */
export default function ProfilePage() {
  const form = useQuery({ queryKey: ['registration-form'], queryFn: getRegistrationForm, staleTime: 0 })
  if (form.isPending) return <p role="status">Loading…</p>
  if (form.isError) return <ErrorNotice error={form.error} />
  return <ProfileForm form={form.data} />
}

function ProfileForm({ form }: { form: NonNullable<ReturnType<typeof useQuery<Awaited<ReturnType<typeof getRegistrationForm>>>>['data']> }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const save = useMutation({
    mutationFn: (d: Details) => saveProfile(d),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['application'] })
      await queryClient.invalidateQueries({ queryKey: ['consent'] })
      await queryClient.invalidateQueries({ queryKey: ['registration-form'] })
      navigate('/', { replace: true })
    },
  })
  const fieldErrors = save.error instanceof ApiError ? save.error.fields : {}
  const locked = save.error instanceof ApiError && save.error.code === 'PROFILE_LOCKED'
  return (
    <div>
      <h1>About you</h1>
      <p className="muted">{form.campaignTitle}: we use these details for your admission application.</p>
      {locked
        ? <div role="alert" className="notice error">Your details can no longer be changed here. Contact admissions if something is wrong.</div>
        : save.error && Object.keys(fieldErrors).length === 0 && <ErrorNotice error={save.error} />}
      <DetailsForm
        idPrefix="profile"
        guardianRequired={form.guardianConsentRequired}
        fields={form.fields}
        initial={{ name: form.core.name ?? '', mobile: form.core.mobile ?? '', dateOfBirth: form.core.dateOfBirth ?? '', values: toAnswers(form.fields, form.values), guardian: form.guardian }}
        errors={fieldErrors}
        busy={save.isPending}
        submitLabel="Save and continue"
        onSubmit={(d) => save.mutate(d)}
      />
    </div>
  )
}
